import { motion } from "framer-motion";
import { usePage } from "hooks/usePage";
import { Briefcase, ChartPie, TrendingUp } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import PageWrapper from "../../components/layout/page-wrapper";
import MarketGrowth from "./sections/MarketGrowth";
import PerformanceRate from "./sections/PerformanceRate";
import RiFacPremium from "./sections/RiFacPremium";
import ShareComponent from "./sections/Share";
import TotalRevenue from "./sections/TotalRevenue";
import TotalRevenuePie from "./sections/TotalRevenuePie";

type Props = {};

type TabSection = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

const Dashboard: FC<Props> = () => {
  usePage("Kava | Dashboard");

  const sections: TabSection[] = [
    {
      id: "premiums",
      label: "Premiums & Growth",
      icon: <TrendingUp size={16} />,
    },
    {
      id: "shares",
      label: "Shares & Performance",
      icon: <Briefcase size={16} />,
    },
    {
      id: "revenue",
      label: "Revenue",
      icon: <ChartPie size={16} />,
    },
  ];

  const [activeTab, setActiveTab] = useState<string>(sections[0].id);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const registerSectionRef = (id: string, ref: HTMLDivElement | null) => {
    sectionRefs.current[id] = ref;
  };

  const scrollToSection = (id: string) => {
    setActiveTab(id);
    requestAnimationFrame(() => {
      sectionRefs.current[id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) {
          const id = visible.target.id;
          if (id && id !== activeTab) {
            setActiveTab(id);
          }
        }
      },
      {
        rootMargin: "-100px 0px -200px 0px",
        threshold: 0.6,
      }
    );

    const refs = Object.values(sectionRefs.current);
    refs.forEach((ref) => ref && observer.observe(ref));

    return () => {
      refs.forEach((ref) => ref && observer.unobserve(ref));
    };
  }, [activeTab]);

  return (
    <PageWrapper hidePadding>
      <div className="flex flex-col h-full">
        {/* Tabs Section */}
        <div className="bg-green-500 p-4">
          <div className="relative flex space-x-4">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`relative flex items-center px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                  activeTab === section.id
                    ? "text-green-700 bg-white"
                    : "text-white"
                }`}
              >
                <span className="mr-2">{section.icon}</span>
                {section.label}
                {activeTab === section.id && (
                  <motion.div
                    layoutId="tab-indicator"
                    className="absolute inset-0 bg-white rounded-md z-[-1]"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-8">
            {/* Premiums & Growth Section */}
            <div
              id="premiums"
              ref={(ref) => registerSectionRef("premiums", ref)}
              className=" py-6"
            >
              <h2 className="text-2xl font-bold mb-4">Premiums & Growth</h2>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <RiFacPremium />
                <MarketGrowth />
              </motion.div>
            </div>

            {/* Shares & Performance Section */}
            <div
              id="shares"
              ref={(ref) => registerSectionRef("shares", ref)}
              className="py-6"
            >
              <h2 className="text-2xl font-bold mb-4">Shares & Performance</h2>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <ShareComponent />
                <PerformanceRate />
              </motion.div>
            </div>

            {/* Revenue Section */}
            <div
              id="revenue"
              ref={(ref) => registerSectionRef("revenue", ref)}
              className="py-6"
            >
              <h2 className="text-2xl font-bold mb-4">Revenue</h2>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <TotalRevenue />
                <TotalRevenuePie />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default Dashboard;
